import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, FileDown, Link2, MessageSquareShare, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { downloadDeckPdf } from "@/lib/deckPdf";
import { buildLink, directLink, shareMessage } from "./deckRegistry";
import { LinkPreview } from "./LinkPreview";

const kopier = (tekst, melding) =>
  navigator.clipboard.writeText(tekst).then(() => toast.success(melding)).catch(() => toast.error("Kunne ikke kopiere"));

export const lastNedPdf = async (deck, mottaker, kategori, setBusy) => {
  setBusy(true);
  const id = toast.loading("Genererer PDF …");
  try {
    await downloadDeckPdf({
      deck: deck.id, mottaker, kategori: deck.category ? kategori : "", filePrefix: deck.filePrefix,
      onProgress: (p) => toast.loading(p >= 100 ? "Laster ned …" : `Genererer PDF · ${p}%`, { id }),
    });
    toast.success("PDF lastet ned", { id });
  } catch (e) { toast.error(e?.message || formatApiError(e), { id }); } finally { setBusy(false); }
};

const Chips = ({ items, onPick, testid }) => (
  <div className="pdadm-chips">
    {items.map((s) => (
      <button key={s} type="button" className="pdadm-chip" onClick={() => onPick(s)} data-testid={`${testid}-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</button>
    ))}
  </div>
);

export const LinkBuilder = ({ deck, password, onSaved }) => {
  const [mottaker, setMottaker] = useState("");
  const [kategori, setKategori] = useState("");
  const [flags, setFlags] = useState([]);
  const [rolle, setRolle] = useState("");
  const [busy, setBusy] = useState(false);
  const [lagrer, setLagrer] = useState(false);
  useEffect(() => { setMottaker(""); setKategori(""); setFlags([]); setRolle(""); }, [deck.id]);

  const m = mottaker.trim();
  /* Flagg («-bergen», rolle «-programkomite») bæres i kategorifeltet så token/PDF/print får det uten egen kanal */
  const k = deck.category ? [kategori.trim(), ...flags, rolle].filter(Boolean).join(" ") : "";
  const [token, setToken] = useState("");
  const [tokenFeil, setTokenFeil] = useState(false);
  const [forsok, setForsok] = useState(0);
  useEffect(() => {
    if (!m) { setToken(""); setTokenFeil(false); return undefined; }
    let live = true;
    setTokenFeil(false);
    const hent = (rest) => api.get(`/admin/deck/${deck.id}/token`, { params: { mottaker: m, kategori: k } })
      .then(({ data }) => live && setToken(data.token))
      .catch(() => { if (!live) return; if (rest > 0) setTimeout(() => live && hent(rest - 1), 1200); else { setToken(""); setTokenFeil(true); } });
    const t = setTimeout(() => hent(2), 350);
    return () => { live = false; clearTimeout(t); };
  }, [deck.id, m, k, forsok]);
  const klar = !m || !!token;
  const link = buildLink(deck, token);
  const direkte = directLink(deck, token);
  const visning = deck.changes(m, k);

  const lagre = async () => {
    if (!m) return;
    setLagrer(true);
    try {
      const { data } = await api.post("/admin/deck-lenker", { deck: deck.id, mottaker: m, kategori: deck.category ? k : "" });
      toast.success(data.existed ? `${m} finnes allerede i listen` : `Lenke til ${m} lagret`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setLagrer(false); }
  };

  return (
    <div className="pdadm-builder adm-card" data-testid="pdadm-builder">
      <div className="pdadm-builder-in">
        <p className="pdadm-step"><span>2</span>Skriv inn mottaker</p>
        <label className="adm-label mt-4">Mottaker
          <input
            className="adm-input pdadm-in-lg"
            value={mottaker}
            onChange={(e) => setMottaker(e.target.value)}
            placeholder={deck.recipient.placeholder}
            autoFocus
            data-testid="pdadm-mottaker-input"
          />
        </label>
        <Chips items={deck.recipient.suggestions} onPick={(s) => { setMottaker(s); const p = deck.recipient.presets?.[s]; if (p !== undefined) { const t = p.split(/\s+/); const roller = Object.keys(deck.roles || {}); setKategori(t.filter((x) => !x.startsWith("-")).join(" ")); setFlags(t.filter((x) => x.startsWith("-") && !roller.includes(x))); setRolle(t.find((x) => roller.includes(x)) || ""); } }} testid="pdadm-chip-mottaker" />
        {deck.category?.variants && (
          <>
            <label className="adm-label mt-5">Variant <em>(hvilken versjon decket får)</em>
              <select className="adm-input" value={kategori} onChange={(e) => setKategori(e.target.value)} data-testid="pdadm-variant-select">
                {Object.entries(deck.category.variants).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {kategori === "internal" && <p className="pdadm-hint mt-2">Internal vises kun i admin-forhåndsvisningen — en offentlig lenke med denne varianten faller tilbake til General.</p>}
          </>
        )}
        {deck.roles && (
          <label className="adm-label mt-4">Rolle <em>(gjør «Måter å bli med» og «Neste steg» personlige)</em>
            <select className="adm-input" value={rolle} onChange={(e) => setRolle(e.target.value)} data-testid="pdadm-rolle-select">
              {Object.entries(deck.roles).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        )}
        {deck.category && !deck.category.variants && (
          <>
            <label className="adm-label mt-5">Kategori <em>(valgfritt — brukes i «One … partner»)</em>
              <input
                className="adm-input"
                value={kategori}
                onChange={(e) => setKategori(e.target.value.toLowerCase())}
                placeholder={deck.category.placeholder}
                data-testid="pdadm-kategori-input"
              />
            </label>
            <Chips items={deck.category.suggestions} onPick={setKategori} testid="pdadm-chip-kategori" />
            {deck.toggles?.map(({ flag, label }) => (
              <label key={flag} className="pdadm-toggle mt-4" data-testid={`pdadm-toggle${flag}`}>
                <input type="checkbox" checked={flags.includes(flag)} onChange={(e) => setFlags((f) => (e.target.checked ? [...f, flag] : f.filter((x) => x !== flag)))} data-testid={`pdadm-toggle${flag}-input`} />
                <span>{label}</span>
              </label>
            ))}
          </>
        )}
        {!deck.category && <p className="pdadm-hint mt-4" data-testid="pdadm-recipient-hint">{deck.recipient?.hint || <>Kategorien er fast: <b>Build Partner</b>. Kun mottakernavnet endres.</>}</p>}
      </div>

      <div className="pdadm-builder-out">
        <p className="pdadm-step"><span>3</span>Slik tilpasses decket</p>
        <dl className="pdadm-changes" data-testid="pdadm-changes">
          {visning.map(([slide, tekst]) => (
            <div key={slide}><dt>{slide}</dt><dd>{tekst}</dd></div>
          ))}
        </dl>
        <p className="pdadm-step mt-6"><span>4</span>Kopier og send</p>
        <div className={`pdadm-link ${tokenFeil ? "err" : ""}`} data-testid="pdadm-link">
          <Link2 size={13} />
          <span className="u" title={link}>{klar ? link.replace(/^https?:\/\//, "") : tokenFeil ? "Kunne ikke lage anonym lenke — sjekk innlogging/nett" : "Lager anonym lenke …"}</span>
          {tokenFeil && <button type="button" className="pdadm-retry" onClick={() => setForsok((n) => n + 1)} data-testid="pdadm-token-retry">Prøv igjen</button>}
        </div>
        <div className="pdadm-actions">
          <button type="button" className="adm-btn" disabled={!klar} onClick={() => kopier(link, "Lenke kopiert")} data-testid="pdadm-kopier-lenke">
            <Copy size={13} /> Kopier lenke
          </button>
          <button type="button" className="adm-btn-ghost" disabled={!klar} onClick={() => kopier(shareMessage(deck, m, link, password), "Lenke + passord kopiert")} data-testid="pdadm-kopier-melding">
            <MessageSquareShare size={13} /> Lenke + passord
          </button>
          {klar ? (
            <a className="adm-btn-ghost" href={direkte} target="_blank" rel="noreferrer" data-testid="pdadm-aapne">
              <ExternalLink size={13} /> Åpne
            </a>
          ) : (
            <button type="button" className="adm-btn-ghost" disabled data-testid="pdadm-aapne" title="Venter på anonym lenke">
              <ExternalLink size={13} /> Åpne
            </button>
          )}
          <button type="button" className="adm-btn-ghost" disabled={busy} onClick={() => lastNedPdf(deck, m, k, setBusy)} data-testid="pdadm-pdf">
            <FileDown size={13} /> {busy ? "Genererer …" : "PDF"}
          </button>
          <span className="flex-1" />
          <button type="button" className="adm-btn-ghost" disabled={!m || lagrer} onClick={lagre} data-testid="pdadm-lagre">
            <Save size={13} /> {lagrer ? "Lagrer …" : "Lagre i listen"}
          </button>
        </div>
        {!m && <p className="pdadm-hint mt-3">{["build", "quantum"].includes(deck.id) ? `${deck.name}-decket finnes ikke generisk — skriv inn mottaker for å få en lenke.` : "Uten mottaker er dette den generiske lenken."}</p>}
        {m && klar && <p className="pdadm-hint mt-3">Mottakeren ligger kryptert i lenken (<code>?r=…</code>) — navnet står ikke i URL-en og kan ikke byttes ut.</p>}
        <p className="pdadm-step mt-6"><span>5</span>Slik ser lenken ut når den deles</p>
        <LinkPreview deck={deck} mottaker={m} token={token} />
      </div>
    </div>
  );
};
